import type { ColumnDef } from "@tanstack/react-table";
import type { AxiosError } from "axios";
import axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import styled from "styled-components/macro";
import useSWR from "swr";
import { useAuthContext } from "../../../../../components/Auth";
import {
  EditButton,
  GoBackButtonSmall,
  InvisibleButton,
} from "../../../../../components/Buttons/Buttons";
import { DelayedSpinner } from "../../../../../components/DelayedSpinner/DelayedSpinner";
import { IconGroup } from "../../../../../components/DocumentCard/DocumentCard";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { Table } from "../../../../../components/Table/Table";
import { ToggleSwitchV2 } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import { Form } from "../../../../../layout/FormLayout";
import { PageWrapper } from "../../../../../layout/portalPageLayout";
import { ContentWrapper } from "../../../../../layout/publicPageLayout";
import type { OptionType } from "../../../../../types/types";
import type {
  AttributeInput,
  AttributeTemplateSchema,
} from "../../../../../types/types.PIM";
import { useRoutePath } from "../../../../../util/Routing";
import { strings } from "../../../../../util/strings";
import { rowHover, useStoreState } from "../../../../../util/util";
import {
  ActiveCheckIcon,
  DestructiveXIcon,
} from "../components/ProductCollectionSchemaTable/ProductCollectionSchema.util";
import { TemplatesNav } from "./TemplatesNav";
import { LoadingIcon } from "../../../../../components/Icons/Icons";

const SettingsSection = styled.div`
  margin: 24px 0;
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  padding-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${({ theme }) => theme.primaryTextColor};
  margin: 0;
`;

const SettingRow = styled.div`
  margin: 16px 0;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.secondaryTextColor};
  margin: 0;
`;

const IconGroupJustifyEnd = styled(IconGroup)`
  justify-content: flex-end;
`;

const TAG_ATTRIBUTE_INPUT_TYPE: AttributeInput[] = [
  "date",
  "form_field",
  "multiline_entry",
  "multi_select",
  "numeric",
  "single_select",
  "toggle",
  "checkbox",
];

type TableRow = {
  id: string;
  schema_tag: "name" | "description";
  attribute: JSX.Element | string;
  edit: JSX.Element;
};

type FormValues = {
  name_attribute: OptionType<string>;
  description_attribute: OptionType<string>;
};

export function TemplateSettings() {
  const { t } = useTranslation();
  const {
    tenant_id,
    storefront_metadata: { edition },
  } = useStoreState();
  const { template_id } = useParams<{ template_id: string }>();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { hasPermission } = useAuthContext();
  const location = useLocation();
  const { adminPath } = useRoutePath();

  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<"name" | "description" | null>(null);
  const [isSubmittingSchemaTagsUpdate, setIsSubmittingSchemaTagsUpdate] =
    useState(false);
  const [isSubmittingTemplateUpdate, setIsSubmittingTemplateUpdate] =
    useState(false);

  const { control, errors, handleSubmit } = useForm();

  const {
    data: template,
    error: template_error,
    mutate,
  } = useSWR<AttributeTemplateSchema, AxiosError>(
    `/v2/tenants/${tenant_id}/pim/templates/${template_id}`
  );

  const handleSettingChange = async (
    field: keyof AttributeTemplateSchema,
    value: boolean
  ) => {
    setIsSubmittingTemplateUpdate(true);
    try {
      await axios.patch(
        `/v2/tenants/${tenant_id}/pim/templates/${template_id}`,
        { [field]: value }
      );
      notifySuccess(t("Settings updated successfully"));
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage ? errorMessage : t("Failed to update settings"),
        { error }
      );
    } finally {
      await mutate();
      setIsSubmittingTemplateUpdate(false);
    }
  };

  const handleEnableSchemaTags = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    handleSettingChange("show_schema_tag", isChecked);
  };

  const get_default_tag_name = (
    template: AttributeTemplateSchema | undefined
  ) => {
    if (!template) return undefined;
    return template.groups
      .flatMap(({ attributes }) => attributes)
      .find(
        (attr) =>
          attr.object_type === "product_name" &&
          !attr.is_restricted &&
          !attr.is_not_applicable
      );
  };

  const get_default_tag_description = (
    template: AttributeTemplateSchema | undefined
  ) => {
    if (!template) return undefined;
    return template.groups
      .flatMap(({ attributes }) => attributes)
      .find(
        (attr) =>
          attr.object_type === "product_description" &&
          !attr.is_not_applicable &&
          !attr.is_restricted
      );
  };

  const onSubmit = async (formValues: FormValues) => {
    const { name_attribute, description_attribute } = formValues;
    const default_tag_name = get_default_tag_name(template);
    const default_tag_description = get_default_tag_description(template);
    setIsSubmittingSchemaTagsUpdate(true);
    try {
      const body = {
        tag_name_attribute_id:
          selectedRow === "name"
            ? name_attribute.value
            : template?.schema_tags?.tag_name_attribute?.id ??
              default_tag_name?.id ??
              "",
        tag_description_attribute_id:
          selectedRow === "description"
            ? description_attribute.value
            : template?.schema_tags?.tag_description_attribute?.id ??
              default_tag_description?.id ??
              "",
      };
      await axios.patch(
        `/v2/tenants/${tenant_id}/pim/templates/${template_id}/attribute-tags`,
        body
      );
      await mutate();
      notifySuccess(t("Schema tags updated successfully"));
      setEditMode(false);
      setSelectedRow(null);
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage ? errorMessage : t("Failed to update schema tags"),
        { error }
      );
    } finally {
      setIsSubmittingSchemaTagsUpdate(false);
    }
  };

  const tableColumns = useMemo<ColumnDef<TableRow>[]>(
    () => [
      {
        header: t("Schema Tag"),
        accessorKey: "schema_tag",
      },
      {
        header: t("Attribute"),
        accessorKey: "attribute",
        cell: (cell) => cell.getValue(),
      },
      {
        header: "",
        accessorKey: "edit",
        cell: (cell) => cell.getValue(),
      },
    ],
    [t]
  );

  useEffect(() => {
    const get_group_attribute_options = () => {
      if (!template) return [];
      return template.groups
        .flatMap(({ attributes }) => attributes)
        .reduce<OptionType<string>[]>((acc, curr) => {
          if (
            curr.is_not_applicable ||
            curr.is_restricted ||
            !TAG_ATTRIBUTE_INPUT_TYPE.includes(curr.input_type)
          ) {
            return acc;
          }
          return [
            ...acc,
            { label: curr.display_name ?? curr.name, value: curr.id },
          ];
        }, []);
    };
    const handleSchemaTagsData = (
      schemaTag: AttributeTemplateSchema["schema_tags"]
    ) => {
      const {
        tag_name_attribute: tag_name,
        tag_description_attribute: tag_description,
      } = schemaTag ?? {
        tenant_id,
        tag_description_attribute: null,
        tag_name_attribute: null,
      };
      const is_editing_row = (editing_row: "name" | "description") =>
        editMode && selectedRow === editing_row;
      const default_tag_name = get_default_tag_name(template);
      const default_tag_description = get_default_tag_description(template);
      const attr_name_display_name = tag_name?.display_name;
      const attr_name_name = tag_name?.name;
      const attr_description_display_name = tag_description?.display_name;
      const attr_description_name = tag_description?.name;
      const name_default_value =
        tag_name && !tag_name.is_not_applicable && !tag_name.is_restricted
          ? {
              label: tag_name.display_name ?? tag_name.name ?? "--",
              value: tag_name.id,
            }
          : default_tag_name
          ? {
              label:
                default_tag_name?.display_name ??
                default_tag_name?.name ??
                "--",
              value: default_tag_name?.id ?? null,
            }
          : { label: "--", value: null };
      const description_default_value =
        tag_description &&
        !tag_description.is_not_applicable &&
        !tag_description.is_restricted
          ? {
              label:
                tag_description?.display_name ?? tag_description?.name ?? "--",
              value: tag_description?.id ?? null,
            }
          : default_tag_description
          ? {
              label:
                default_tag_description?.display_name ??
                default_tag_description?.name ??
                "--",
              value: default_tag_description?.id ?? null,
            }
          : { label: "--", value: null };
      setTableData([
        {
          id: tag_name?.id ?? "0",
          schema_tag: t("Name"),
          attribute: is_editing_row("name") ? (
            <Controller
              as={SelectBoxV2}
              control={control}
              name={"name_attribute"}
              placeholder={t("Select an attribute")}
              normalPlaceholder={true}
              menuPortalTarget={document.querySelector("body")}
              options={get_group_attribute_options()}
              rules={{
                required: true,
                validate: (val: OptionType<string> | null) =>
                  !!val?.value || strings(t).thisIsARequiredField,
              }}
              defaultValue={name_default_value}
              errors={errors}
              formState={{ submitCount: 1 }}
            />
          ) : (
            attr_name_display_name ??
            attr_name_name ??
            default_tag_name?.display_name ??
            default_tag_name?.name ??
            "--"
          ),
          edit: (
            <IconGroupJustifyEnd>
              {is_editing_row("name") ? (
                <>
                  <InvisibleButton
                    disabled={isSubmittingSchemaTagsUpdate}
                    type="submit"
                  >
                    <ActiveCheckIcon />
                  </InvisibleButton>
                  <InvisibleButton
                    type="button"
                    onClick={() => {
                      setEditMode(false);
                      setSelectedRow(null);
                    }}
                  >
                    <DestructiveXIcon />
                  </InvisibleButton>
                </>
              ) : (
                <EditButton
                  type="button"
                  disabled={editMode || !hasPermission("modify_templates")}
                  onClick={() => {
                    setEditMode(true);
                    setSelectedRow("name");
                  }}
                />
              )}
            </IconGroupJustifyEnd>
          ),
        },
        {
          id: tag_description?.id ?? "1",
          schema_tag: t("Description"),
          attribute: is_editing_row("description") ? (
            <Controller
              as={SelectBoxV2}
              control={control}
              name={"description_attribute"}
              placeholder={t("Select an attribute")}
              normalPlaceholder={true}
              menuPortalTarget={document.querySelector("body")}
              options={get_group_attribute_options()}
              rules={{
                required: true,
                validate: (val: OptionType<string> | null) =>
                  !!val?.value || strings(t).thisIsARequiredField,
              }}
              defaultValue={description_default_value}
              errors={errors}
              formState={{ submitCount: 1 }}
            />
          ) : (
            attr_description_display_name ??
            attr_description_name ??
            default_tag_description?.display_name ??
            default_tag_description?.name ??
            "--"
          ),
          edit: (
            <IconGroupJustifyEnd>
              {is_editing_row("description") ? (
                <>
                  <InvisibleButton
                    disabled={isSubmittingSchemaTagsUpdate}
                    type="submit"
                  >
                    <ActiveCheckIcon />
                  </InvisibleButton>
                  <InvisibleButton
                    type="button"
                    onClick={() => {
                      setEditMode(false);
                      setSelectedRow(null);
                    }}
                  >
                    <DestructiveXIcon />
                  </InvisibleButton>
                </>
              ) : (
                <EditButton
                  type="button"
                  disabled={editMode || !hasPermission("modify_templates")}
                  onClick={() => {
                    setEditMode(true);
                    setSelectedRow("description");
                  }}
                />
              )}
            </IconGroupJustifyEnd>
          ),
        },
      ]);
    };
    if (template) {
      handleSchemaTagsData(
        template?.schema_tags ?? {
          tenant_id,
          tag_name_attribute: null,
          tag_description_attribute: null,
        }
      );
    }
  }, [
    control,
    editMode,
    errors,
    hasPermission,
    isSubmittingSchemaTagsUpdate,
    selectedRow,
    t,
    template,
    tenant_id,
  ]);

  if (!template && !template_error) {
    return <DelayedSpinner />;
  }

  if (template_error) {
    return (
      <ErrorPlaceholder
        message={t(
          "There was an error fetching the template details. Please try again later."
        )}
      />
    );
  }

  return (
    <PageWrapper>
      <div style={{ position: "relative" }}>
        <Link
          to={
            location.search.includes("from=dashboard")
              ? `${adminPath}/dashboard`
              : `${adminPath}/pim/templates`
          }
        >
          <GoBackButtonSmall text="Back" />
        </Link>
      </div>
      <TemplatesNav
        pageTitle={template?.template_name ?? ""}
        tabIndex={5}
        templateID={template_id}
      />
      <ContentWrapper>
        <SettingsSection>
          <SectionTitle>{t("Variant Products")}</SectionTitle>
          <Description>
            {t("Enable / disable master & variant products on this template.")}
          </Description>
          <SettingRow>
            {isSubmittingTemplateUpdate ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  width: "fit-content",
                }}
              >
                <LoadingIcon width={24} height={24} />
              </div>
            ) : (
              <ToggleSwitchV2
                label={t("Enable Variant Products")}
                name="variant_products_toggle"
                checked={template?.enable_variant_products}
                onChange={(e) =>
                  handleSettingChange(
                    "enable_variant_products",
                    e.target.checked
                  )
                }
                disabled={!hasPermission("modify_templates")}
              />
            )}
          </SettingRow>
        </SettingsSection>
        <SettingsSection>
          <SectionTitle>{t("Completion Scores")}</SectionTitle>
          <Description>
            {t(
              "Configure which completion scores are enabled for this template."
            )}
          </Description>

          <SettingRow>
            <ToggleSwitchV2
              label={t("Enable Total Completion Score")}
              name="show_total_completion_score"
              defaultChecked={template?.show_total_completion_score}
              onChange={(e) =>
                handleSettingChange(
                  "show_total_completion_score",
                  e.target.checked
                )
              }
              disabled={!hasPermission("modify_templates")}
            />
          </SettingRow>

          <SettingRow>
            <ToggleSwitchV2
              label={t("Enable Mandatory Completion Score")}
              name="show_mandatory_completion_score"
              defaultChecked={template?.show_mandatory_completion_score}
              onChange={(e) =>
                handleSettingChange(
                  "show_mandatory_completion_score",
                  e.target.checked
                )
              }
              disabled={!hasPermission("modify_templates")}
            />
          </SettingRow>
        </SettingsSection>
        {edition !== "pim" && (
          <SettingsSection>
            <SectionTitle>{t("Schema Tags")}</SectionTitle>
            <SettingRow>
              <ToggleSwitchV2
                label={t("Enable Schema Tags")}
                name="show_schema_tag"
                defaultChecked={template?.show_schema_tag}
                onChange={handleEnableSchemaTags}
                disabled={!hasPermission("modify_templates")}
              />
            </SettingRow>
            {template?.show_schema_tag && (
              <SettingRow>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <Table
                    columns={tableColumns}
                    data={tableData}
                    lastChildleftAlign
                    isLoading={false}
                    error={undefined}
                    rowHover={rowHover}
                  />
                </Form>
              </SettingRow>
            )}
          </SettingsSection>
        )}
      </ContentWrapper>
    </PageWrapper>
  );
}
